<!-- src/views/Restaurante/MesasList.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">

    <!-- HEADER PREMIUM -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1.5">
          <div class="w-10 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-blue-600 dark:text-blue-400 tracking-widest uppercase">Módulo Restaurante</span>
        </div>
        <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-3">
          Mesas
          <span class="px-3 py-1 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 text-xs sm:text-sm font-black rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            {{ mesas.length }}
          </span>
        </h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Administra las mesas y sus códigos QR</p>
      </div>

      <div class="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
        <div class="hidden sm:flex bg-white dark:bg-gray-800 p-1.5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <button @click="viewMode = 'table'"
            :class="['p-2 rounded-lg transition-all flex items-center',
              viewMode === 'table' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-400 hover:text-gray-600']">
            <Icon icon="mdi:table" class="text-lg" />
          </button>
          <button @click="viewMode = 'cards'"
            :class="['p-2 rounded-lg transition-all flex items-center',
              viewMode === 'cards' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-400 hover:text-gray-600']">
            <Icon icon="mdi:view-grid" class="text-lg" />
          </button>
        </div>

        <button @click="openCreateModal"
          class="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-black text-xs sm:text-sm tracking-wide transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2">
          <Icon icon="mdi:plus-circle" class="text-lg" />
          <span>Nueva Mesa</span>
        </button>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6 animate-fadeInUp">
      <div class="flex flex-col sm:flex-row gap-4 items-end">
        <div class="flex-1 w-full">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Buscar Mesas</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Buscar por número u observación..."
              class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 rounded-xl transition-all outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>

        <div class="w-full sm:w-44">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Estado</label>
          <select v-model="filterEstado"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-blue-500 rounded-xl font-bold text-gray-700 dark:text-gray-200 outline-none transition-all cursor-pointer text-sm appearance-none">
            <option value="all">Todos</option>
            <option value="libre">Libre</option>
            <option value="ocupada">Ocupada</option>
            <option value="reservada">Reservada</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </div>

        <div class="w-full sm:w-48">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1 uppercase">Zona</label>
          <select v-model="filterZona"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-transparent focus:border-blue-500 rounded-xl font-bold text-gray-700 dark:text-gray-200 outline-none transition-all cursor-pointer text-sm appearance-none">
            <option value="all">Todas las zonas</option>
            <option v-for="z in zonas" :key="z.idZonaMesa" :value="z.idZonaMesa">{{ z.nombre }}</option>
          </select>
        </div>

        <button @click="cargarMesas"
          class="w-full sm:w-auto px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2">
          <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- ESTADÍSTICAS -->
    <div class="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-5 mb-6 animate-fadeInUp delay-100">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Total Mesas</p>
            <p class="text-3xl font-black text-gray-900 dark:text-white">{{ estadisticas.total }}</p>
          </div>
          <div class="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
            <Icon icon="mdi:table-chair" class="text-blue-600 dark:text-blue-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10">
          <span class="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-[10px] font-black">{{ mesasFiltradas.length }} filtradas</span>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Libres</p>
            <p class="text-3xl font-black text-emerald-600">{{ estadisticas.libres }}</p>
          </div>
          <div class="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
            <Icon icon="mdi:check-decagram" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-emerald-500">
          <Icon icon="mdi:circle" class="mr-1 text-[8px]" /> Disponibles
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Ocupadas</p>
            <p class="text-3xl font-black text-rose-600">{{ estadisticas.ocupadas }}</p>
          </div>
          <div class="p-2.5 bg-rose-50 dark:bg-rose-900/30 rounded-xl">
            <Icon icon="mdi:account-group" class="text-rose-600 dark:text-rose-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-rose-500">
          <Icon icon="mdi:alert-circle" class="mr-1 text-sm" /> En servicio
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Reservadas</p>
            <p class="text-3xl font-black text-amber-600">{{ estadisticas.reservadas }}</p>
          </div>
          <div class="p-2.5 bg-amber-50 dark:bg-amber-900/30 rounded-xl">
            <Icon icon="mdi:calendar-clock" class="text-amber-600 dark:text-amber-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-amber-500">
          <Icon icon="mdi:clock" class="mr-1 text-sm" /> Con reserva
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
        <div class="flex items-start justify-between relative z-10">
          <div>
            <p class="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-1">Cap. Total</p>
            <p class="text-3xl font-black text-indigo-600">{{ estadisticas.capacidadTotal }}</p>
          </div>
          <div class="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
            <Icon icon="mdi:account-multiple" class="text-indigo-600 dark:text-indigo-400 text-2xl" />
          </div>
        </div>
        <div class="mt-3 relative z-10 flex items-center text-[10px] font-black text-indigo-500">
          <Icon icon="mdi:seat" class="mr-1 text-sm" /> Comensales
        </div>
      </div>
    </div>

    <!-- CONTENIDO -->
    <div class="animate-fadeInUp delay-200">
      <!-- VISTA TABLA -->
      <div v-if="viewMode === 'table'" class="hidden lg:block bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50/80 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Mesa</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase">Zona</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Capacidad</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Estado</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">QR</th>
                <th class="px-6 py-4 text-[11px] font-black text-gray-400 tracking-widest uppercase text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="loading" v-for="i in 3" :key="'load-'+i" class="animate-pulse">
                <td colspan="6" class="px-6 py-5"><div class="h-5 bg-gray-100 dark:bg-gray-700 rounded-lg w-full"></div></td>
              </tr>
              <tr v-if="!loading && mesasFiltradas.length === 0">
                <td colspan="6" class="px-6 py-20 text-center">
                  <div class="flex flex-col items-center opacity-40 select-none">
                    <Icon icon="mdi:table-chair" class="text-8xl mb-4 text-gray-300 dark:text-gray-600" />
                    <h4 class="text-xl font-black text-gray-400 tracking-widest uppercase">No hay mesas</h4>
                    <p class="text-sm text-gray-400 mt-1 font-medium">Registra tu primera mesa para comenzar</p>
                  </div>
                </td>
              </tr>
              <tr v-for="mesa in mesasFiltradas" :key="mesa.idMesa" class="hover:bg-blue-50/40 dark:hover:bg-gray-700/40 transition-all duration-200 group">
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div :class="['h-11 w-11 rounded-xl flex items-center justify-center shadow-sm border',
                      mesa.estado === 'libre' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 dark:from-emerald-900/40 dark:to-teal-900/40 dark:border-emerald-800/50'
                      : mesa.estado === 'ocupada' ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-100 dark:from-rose-900/40 dark:to-pink-900/40 dark:border-rose-800/50'
                      : mesa.estado === 'reservada' ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 dark:from-amber-900/40 dark:to-orange-900/40 dark:border-amber-800/50'
                      : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                      <Icon icon="mdi:table-chair"
                        :class="['text-xl',
                          mesa.estado === 'libre' ? 'text-emerald-600 dark:text-emerald-400'
                          : mesa.estado === 'ocupada' ? 'text-rose-600 dark:text-rose-400'
                          : mesa.estado === 'reservada' ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-400']" />
                    </div>
                    <div>
                      <div class="text-sm font-black text-gray-900 dark:text-white">Mesa {{ mesa.numero }}</div>
                      <div class="text-[11px] font-bold text-gray-400">ID: {{ mesa.idMesa }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-300">{{ mesa.nombreZona || '—' }}</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="px-3 py-1.5 inline-flex items-center text-[11px] font-black uppercase tracking-widest rounded-lg bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/50">
                    <Icon icon="mdi:account-group" class="mr-1.5 text-sm" /> {{ mesa.capacidad }}
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <span :class="['px-3 py-1.5 inline-flex items-center text-[11px] font-black uppercase tracking-widest rounded-lg border',
                    mesa.estado === 'libre' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                    : mesa.estado === 'ocupada' ? 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50'
                    : mesa.estado === 'reservada' ? 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50'
                    : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700']">
                    <Icon :icon="mesa.estado === 'libre' ? 'mdi:check-circle' : mesa.estado === 'ocupada' ? 'mdi:account-group' : mesa.estado === 'reservada' ? 'mdi:clock' : 'mdi:close-circle'" class="mr-1.5 text-sm" />
                    {{ convertirEstadoATexto(mesa.estado) }}
                  </span>
                </td>
                <td class="px-6 py-4 text-center">
                  <button @click="abrirQR(mesa)" class="inline-block p-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-400 hover:shadow-md transition-all" title="Ver QR">
                    <img v-if="mesa.qr_codigo" :src="qrCache[mesa.qr_codigo] || ''" :alt="'QR ' + mesa.numero" class="w-9 h-9 object-contain" />
                    <div v-else class="w-9 h-9 flex items-center justify-center">
                      <Icon icon="mdi:qrcode-remove" class="text-gray-300 text-lg" />
                    </div>
                  </button>
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="flex items-center justify-end gap-2">
                    <button @click.stop="openEditModal(mesa)" title="Editar"
                      class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-500 rounded-lg hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition-all shadow-sm">
                      <Icon icon="mdi:pencil" class="text-lg" />
                    </button>
                    <button @click.stop="abrirModalCambiarEstado(mesa)" title="Cambiar estado"
                      class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-indigo-500 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all shadow-sm">
                      <Icon icon="mdi:swap-horizontal" class="text-lg" />
                    </button>
                    <button @click.stop="abrirModalEliminar(mesa)" title="Desactivar"
                      class="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all shadow-sm">
                      <Icon icon="mdi:delete" class="text-lg" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- VISTA TARJETAS -->
      <div v-if="viewMode === 'cards' || windowWidth < 1024"
        class="lg:grid lg:grid-cols-2 lg:gap-4 xl:grid-cols-3 space-y-4 lg:space-y-0">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 animate-pulse h-48"></div>
        <div v-if="!loading && mesasFiltradas.length === 0"
          class="col-span-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-10 text-center">
          <div class="flex flex-col items-center opacity-40 select-none">
            <Icon icon="mdi:table-chair" class="text-6xl mb-3 text-gray-300 dark:text-gray-600" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest uppercase">No hay mesas</h4>
            <p class="text-sm text-gray-400 mt-1">Crea una nueva mesa para empezar</p>
          </div>
        </div>

        <div v-for="mesa in mesasFiltradas" :key="'card-'+mesa.idMesa"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col">
          <div class="p-5 flex-1">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center gap-3">
                <div :class="['w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border',
                  mesa.estado === 'libre' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 dark:from-emerald-900/40 dark:to-teal-900/40 dark:border-emerald-800/50'
                  : mesa.estado === 'ocupada' ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-100 dark:from-rose-900/40 dark:to-pink-900/40 dark:border-rose-800/50'
                  : mesa.estado === 'reservada' ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 dark:from-amber-900/40 dark:to-orange-900/40 dark:border-amber-800/50'
                  : 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700']">
                  <Icon icon="mdi:table-chair"
                    :class="['text-2xl',
                      mesa.estado === 'libre' ? 'text-emerald-600 dark:text-emerald-400'
                      : mesa.estado === 'ocupada' ? 'text-rose-600 dark:text-rose-400'
                      : mesa.estado === 'reservada' ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-400']" />
                </div>
                <div>
                  <h3 class="font-black text-gray-900 dark:text-white text-base">Mesa {{ mesa.numero }}</h3>
                  <p class="text-xs font-bold text-gray-400 mt-0.5">ID: {{ mesa.idMesa }}</p>
                </div>
              </div>
              <span :class="['px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border',
                mesa.estado === 'libre' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50'
                : mesa.estado === 'ocupada' ? 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50'
                : mesa.estado === 'reservada' ? 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50'
                : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700']">
                {{ convertirEstadoATexto(mesa.estado) }}
              </span>
            </div>

            <div class="flex items-center gap-3">
              <!-- QR Preview -->
              <button @click="abrirQR(mesa)"
                class="flex-shrink-0 p-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 hover:border-blue-400 hover:shadow-md transition-all">
                <img v-if="mesa.qr_codigo" :src="qrCache[mesa.qr_codigo] || ''" :alt="'QR ' + mesa.numero" class="w-14 h-14 object-contain" />
                <div v-else class="w-14 h-14 flex items-center justify-center">
                  <Icon icon="mdi:qrcode-remove" class="text-gray-300 text-2xl" />
                </div>
              </button>
              <div class="flex-1 grid grid-cols-1 gap-1.5">
                <div class="flex items-center gap-1.5 text-xs">
                  <Icon icon="mdi:map-marker" class="text-gray-400 text-sm" />
                  <span class="font-bold text-gray-600 dark:text-gray-300 truncate">{{ mesa.nombreZona || 'Sin zona' }}</span>
                </div>
                <div class="flex items-center gap-1.5 text-xs">
                  <Icon icon="mdi:account-group" class="text-gray-400 text-sm" />
                  <span class="font-bold text-gray-600 dark:text-gray-300">{{ mesa.capacidad }} personas</span>
                </div>
                <div v-if="mesa.observaciones" class="flex items-center gap-1.5 text-xs">
                  <Icon icon="mdi:note-text" class="text-gray-400 text-sm" />
                  <span class="font-medium text-gray-500 dark:text-gray-400 truncate">{{ mesa.observaciones }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 p-3 grid grid-cols-3 gap-2">
            <button @click.stop="openEditModal(mesa)"
              class="py-2.5 bg-white dark:bg-gray-800 text-amber-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-amber-50 transition-colors">
              <Icon icon="mdi:pencil" class="text-base sm:text-sm" /><span>Editar</span>
            </button>
            <button @click.stop="abrirModalCambiarEstado(mesa)"
              class="py-2.5 bg-white dark:bg-gray-800 text-indigo-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-indigo-50 transition-colors">
              <Icon icon="mdi:swap-horizontal" class="text-base sm:text-sm" /><span>Estado</span>
            </button>
            <button @click.stop="abrirModalEliminar(mesa)"
              class="py-2.5 bg-white dark:bg-gray-800 text-rose-600 border border-gray-200 dark:border-gray-600 rounded-xl font-black text-[10px] flex flex-col sm:flex-row items-center justify-center gap-1 hover:bg-rose-50 transition-colors">
              <Icon icon="mdi:delete" class="text-base sm:text-sm" /><span>Borrar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ MODAL FORMULARIO (ESTILO ZonasMesaList) ============ -->
    <Teleport to="body">
      <div v-if="showModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <!-- Header gradiente -->
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingMesa ? 'mdi:pencil-box' : 'mdi:table-chair'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingMesa ? 'Editar Mesa' : 'Nueva Mesa' }}</h3>
              <p class="text-[11px] text-white/80 font-bold">{{ editingMesa ? 'Modifica los datos' : 'Completa los datos' }}</p>
            </div>
            <button @click="closeModal" class="ml-auto p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>

          <!-- Body con scroll -->
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveMesa" class="space-y-4">

              <!-- Número -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Número de Mesa *</label>
                <input v-model="form.numero" type="text" required maxlength="20"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all"
                  placeholder="Ej: M01, 5, VIP-1" />
              </div>

              <!-- Capacidad + Zona -->
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Capacidad</label>
                  <input v-model.number="form.capacidad" type="number" min="1" max="50" required
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Zona</label>
                  <select v-model="form.idZonaMesa"
                    class="w-full px-3 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all">
                    <option :value="null">Sin zona</option>
                    <option v-for="z in zonas" :key="z.idZonaMesa" :value="z.idZonaMesa">{{ z.nombre }}</option>
                  </select>
                </div>
              </div>

              <!-- Estado -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-3 uppercase">Estado</label>
                <div class="grid grid-cols-4 gap-2">
                  <label v-for="opt in estadosOptions" :key="opt.value"
                    :class="['flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl border-2 font-black text-[10px] cursor-pointer transition-all uppercase tracking-widest',
                      form.estado === opt.value
                        ? opt.activeClass
                        : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700']">
                    <input type="radio" v-model="form.estado" :value="opt.value" class="hidden" />
                    <Icon :icon="opt.icon" class="text-base" />
                    {{ opt.label }}
                  </label>
                </div>
              </div>

              <!-- 🔥 QR AUTO-GENERADO (no editable) -->
              <div class="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-4 border-2 border-indigo-200 dark:border-indigo-800/50">
                <div class="flex items-center gap-2 mb-3">
                  <Icon icon="mdi:qrcode" class="text-indigo-600 dark:text-indigo-400 text-lg" />
                  <label class="text-[10px] font-black text-indigo-700 dark:text-indigo-300 tracking-widest uppercase">
                    Código QR (auto-generado)
                  </label>
                  <span class="ml-auto px-2 py-0.5 bg-indigo-600 text-white rounded-md text-[9px] font-black uppercase">Sistema</span>
                </div>

                <div class="flex items-center gap-4">
                  <!-- Preview QR grande -->
                  <div class="flex-shrink-0 p-2 bg-white rounded-2xl shadow-md border-2 border-indigo-100">
                    <img v-if="form.qr_codigo && qrPreview"
                      :src="qrPreview"
                      alt="QR Preview"
                      class="w-24 h-24 object-contain" />
                    <div v-else class="w-24 h-24 flex items-center justify-center bg-gray-50 rounded-xl">
                      <Icon icon="mdi:qrcode-scan" class="text-gray-300 text-4xl" />
                    </div>
                  </div>

                  <div class="flex-1 min-w-0">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Código</p>
                    <div class="px-3 py-2 bg-white dark:bg-gray-800 rounded-lg border border-indigo-200 dark:border-indigo-800/50">
                      <p class="text-[11px] font-mono font-bold text-indigo-700 dark:text-indigo-300 break-all">
                        {{ form.qr_codigo || 'Se generará al guardar...' }}
                      </p>
                    </div>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-2 font-medium leading-snug">
                      <Icon icon="mdi:information-outline" class="inline text-sm mr-0.5" />
                      El QR se genera automáticamente y se muestra en la mesa física para que el cliente lo escanee.
                    </p>
                  </div>
                </div>

                <!-- Botón regenerar (solo en edición) -->
                <button v-if="editingMesa" type="button" @click="regenerarQR"
                  class="mt-3 w-full py-2 bg-white dark:bg-gray-800 border-2 border-indigo-200 dark:border-indigo-800/50 text-indigo-600 dark:text-indigo-400 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-50 transition-all flex items-center justify-center gap-2">
                  <Icon icon="mdi:refresh" class="text-base" />
                  Regenerar QR
                </button>
              </div>

              <!-- Observaciones -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 uppercase">Observaciones</label>
                <textarea v-model="form.observaciones" rows="2" maxlength="200"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                  placeholder="Opcional..."></textarea>
              </div>

              <!-- Botones -->
              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">
                  Cancelar
                </button>
                <button type="submit" :disabled="saving"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" class="text-lg" />
                  {{ saving ? 'Guardando...' : (editingMesa ? 'Actualizar Mesa' : 'Crear Mesa') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============ MODAL QR AMPLIADO ============ -->
    <Teleport to="body">
      <div v-if="showQRModal"
        class="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
        @click.self="showQRModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden animate-zoomIn max-w-sm w-full">
          <div class="bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-4 flex items-center gap-3">
            <Icon icon="mdi:qrcode" class="text-white text-2xl" />
            <div>
              <h3 class="text-base font-black text-white">QR de la Mesa</h3>
              <p class="text-[11px] text-white/80 font-bold">Escanea para abrir el menú</p>
            </div>
            <button @click="showQRModal = false" class="ml-auto p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-6 flex flex-col items-center">
            <div class="p-4 bg-white rounded-2xl shadow-lg border-2 border-indigo-100">
              <img v-if="qrModalImage" :src="qrModalImage" alt="QR Grande" class="w-64 h-64 object-contain" />
              <div v-else class="w-64 h-64 flex items-center justify-center bg-gray-50 rounded-xl">
                <Icon icon="mdi:qrcode-remove" class="text-gray-300 text-6xl" />
              </div>
            </div>
            <div class="mt-4 text-center">
              <p class="text-sm font-black text-gray-900 dark:text-white">Mesa {{ qrModalMesa?.numero }}</p>
              <p class="text-xs font-bold text-gray-500 mt-1">{{ qrModalMesa?.nombreZona || 'Sin zona' }}</p>
              <p class="text-[10px] font-mono text-indigo-600 dark:text-indigo-400 mt-2 break-all px-4">
                {{ qrModalMesa?.qr_codigo }}
              </p>
            </div>
            <button @click="descargarQR(qrModalMesa)"
              class="mt-4 w-full py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg flex items-center justify-center gap-2">
              <Icon icon="mdi:download" class="text-lg" />
              Descargar QR
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============ MODAL CONFIRMACIÓN ============ -->
    <Teleport to="body">
      <div v-if="showConfirmModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion"
              class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all">
              Cancelar
            </button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando"
              class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg"
              :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2';
import QRCode from 'qrcode';
import {
  obtenerMesas, crearMesa, actualizarMesa, eliminarMesa, cambiarEstadoMesa,
  obtenerEstadisticasMesas, buscarMesas, filtrarMesasPorEstado, filtrarMesasPorZona,
  convertirEstadoATexto,
} from '@/api/mesas.js';
import { obtenerZonas } from '@/api/zonasMesa.js';

export default {
  name: 'MesasList',
  components: { Icon },
  data() {
    return {
      mesas: [], zonas: [], loading: false, saving: false, showModal: false,
      searchQuery: '', filterEstado: 'all', filterZona: 'all',
      viewMode: 'table', windowWidth: window.innerWidth,
      form: {
        numero: '', capacidad: 4, idZonaMesa: null,
        estado: 'libre', qr_codigo: '', observaciones: '',
      },
      editingMesa: null,

      // Cache de imágenes QR generadas (por código QR)
      qrCache: {},
      qrPreview: '',   // QR del formulario actual

      // Modal QR ampliado
      showQRModal: false,
      qrModalMesa: null,
      qrModalImage: '',

      // Modal confirmación
      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,

      estadosOptions: [
        { value: 'libre', label: 'Libre', icon: 'mdi:check-circle', activeClass: 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/30' },
        { value: 'ocupada', label: 'Ocupada', icon: 'mdi:account-group', activeClass: 'bg-rose-50 border-rose-500 text-rose-700 dark:bg-rose-900/30' },
        { value: 'reservada', label: 'Reservada', icon: 'mdi:clock', activeClass: 'bg-amber-50 border-amber-500 text-amber-700 dark:bg-amber-900/30' },
        { value: 'inactivo', label: 'Inactivo', icon: 'mdi:close-circle', activeClass: 'bg-gray-200 border-gray-500 text-gray-700 dark:bg-gray-700' },
      ],
    };
  },
  computed: {
    mesasFiltradas() {
      let filtered = [...this.mesas];
      if (this.searchQuery.trim()) filtered = buscarMesas(filtered, this.searchQuery);
      filtered = filtrarMesasPorEstado(filtered, this.filterEstado);
      filtered = filtrarMesasPorZona(filtered, this.filterZona);
      return filtered;
    },
    estadisticas() { return obtenerEstadisticasMesas(this.mesas); },
  },
  watch: {
    // Regenera el preview del QR cuando cambia en el formulario
    'form.qr_codigo'(val) {
      this.generarQrPreview(val);
    },
  },
  async mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);
    await this.cargarZonas();
    await this.cargarMesas();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.windowWidth = window.innerWidth; },

    mostrarToast(icon, title, text = '') {
      Swal.fire({ toast: true, position: 'top-end', icon, title, text, showConfirmButton: false, timer: 3000, timerProgressBar: true });
    },

    // ========== GENERACIÓN DE QR ==========
    // Genera un código único tipo MESA-{numero}-{random}
    generarCodigoQR(numero) {
      const slug = (numero || 'MESA').toString().replace(/\s+/g, '').toUpperCase();
      const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
      const timestamp = Date.now().toString(36).toUpperCase().slice(-4);
      return `MESA-${slug}-${rand}${timestamp}`;
    },

    // Genera la data-url de la imagen QR
    async generarQrPreview(codigo) {
      if (!codigo) { this.qrPreview = ''; return; }
      try {
        // URL que abrirá el cliente al escanear
        const baseUrl = window.location.origin;
        const contenido = `${baseUrl}/menu/${codigo}`;
        this.qrPreview = await QRCode.toDataURL(contenido, {
          width: 300, margin: 1,
          color: { dark: '#1e293b', light: '#ffffff' },
        });
      } catch (e) {
        console.error('Error generando QR preview:', e);
        this.qrPreview = '';
      }
    },

    // Genera (y cachea) la imagen QR de una mesa de la tabla
    async generarImagenQR(mesa) {
      if (!mesa.qr_codigo) return;
      if (this.qrCache[mesa.qr_codigo]) return;
      try {
        const baseUrl = window.location.origin;
        const contenido = `${baseUrl}/menu/${mesa.qr_codigo}`;
        const dataUrl = await QRCode.toDataURL(contenido, {
          width: 200, margin: 0,
          color: { dark: '#1e293b', light: '#ffffff' },
        });
        this.qrCache = { ...this.qrCache, [mesa.qr_codigo]: dataUrl };
      } catch (e) {
        console.error('Error generando QR:', e);
      }
    },

    async regenerarQR() {
      this.form.qr_codigo = this.generarCodigoQR(this.form.numero);
      this.mostrarToast('info', 'QR regenerado', 'Se guardará al confirmar la edición.');
    },

    abrirQR(mesa) {
      if (!mesa.qr_codigo) {
        this.mostrarToast('warning', 'Sin QR', 'Esta mesa no tiene código QR.');
        return;
      }
      this.qrModalMesa = mesa;
      this.qrModalImage = this.qrCache[mesa.qr_codigo] || '';
      this.showQRModal = true;
      // Por si no estaba cacheado
      this.generarImagenQR(mesa).then(() => {
        this.qrModalImage = this.qrCache[mesa.qr_codigo] || '';
      });
    },

    descargarQR(mesa) {
      const dataUrl = this.qrCache[mesa.qr_codigo];
      if (!dataUrl) return;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `QR-Mesa-${mesa.numero}.png`;
      a.click();
    },

    // ========== CARGA ==========
    async cargarMesas() {
      this.loading = true;
      try {
        const r = await obtenerMesas();
        if (r.success) {
          this.mesas = r.data || [];
          // Generar (en background) todas las imágenes QR
          this.mesas.forEach(m => this.generarImagenQR(m));
        }
      } finally { this.loading = false; }
    },
    async cargarZonas() {
      try {
        const r = await obtenerZonas();
        if (r.success) this.zonas = r.data || [];
      } catch {}
    },

    // ========== MODAL FORMULARIO ==========
    openCreateModal() {
      this.editingMesa = null;
      this.form = {
        numero: '', capacidad: 4, idZonaMesa: null,
        estado: 'libre', qr_codigo: this.generarCodigoQR(''), observaciones: '',
      };
      this.qrPreview = '';
      this.showModal = true;
    },
    openEditModal(mesa) {
      this.editingMesa = mesa;
      this.form = {
        numero: mesa.numero, capacidad: mesa.capacidad, idZonaMesa: mesa.idZonaMesa,
        estado: mesa.estado, qr_codigo: mesa.qr_codigo || this.generarCodigoQR(mesa.numero),
        observaciones: mesa.observaciones || '',
      };
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },

    async saveMesa() {
      this.saving = true;
      try {
        // Aseguramos que siempre exista un QR (auto-generado si falta)
        if (!this.form.qr_codigo) {
          this.form.qr_codigo = this.generarCodigoQR(this.form.numero);
        }
        let result;
        if (this.editingMesa) result = await actualizarMesa(this.editingMesa.idMesa, this.form);
        else result = await crearMesa(this.form);

        if (result.success) {
          await this.cargarMesas();
          this.closeModal();
          this.mostrarToast('success', '¡Éxito!', `Mesa ${this.editingMesa ? 'actualizada' : 'creada'} correctamente.`);
        } else {
          this.mostrarToast('error', 'Error', result.error || 'Error al guardar');
        }
      } finally { this.saving = false; }
    },

    // ========== ACCIONES ==========
    abrirModalEliminar(mesa) {
      this.abrirModalConfirmacion({
        titulo: 'Desactivar Mesa', subtitulo: 'Cambiará a inactivo',
        mensaje: `¿Desactivar la mesa "${mesa.numero}"?`,
        detalle: 'La mesa se marcará como inactiva (no se borra).',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700',
        botonTexto: 'Desactivar', datos: mesa.idMesa,
        accion: (id) => this.deleteMesa(id),
      });
    },
    async deleteMesa(id) {
      const r = await eliminarMesa(id);
      if (r.success) {
        await this.cargarMesas();
        this.mostrarToast('success', 'Desactivada', 'La mesa ha sido desactivada.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'No se pudo desactivar.');
      }
    },
    abrirModalCambiarEstado(mesa) {
      const estados = ['libre', 'ocupada', 'reservada', 'inactivo'];
      const nuevo = estados.find(e => e !== mesa.estado);
      this.abrirModalConfirmacion({
        titulo: 'Cambiar Estado', subtitulo: 'Cambiar estado de la mesa',
        mensaje: `¿Cambiar la mesa "${mesa.numero}" a "${convertirEstadoATexto(nuevo)}"?`,
        detalle: `Pasará de "${convertirEstadoATexto(mesa.estado)}" a "${convertirEstadoATexto(nuevo)}".`,
        icon: 'mdi:swap-horizontal',
        color: 'bg-gradient-to-r from-indigo-600 to-blue-600',
        buttonColor: 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700',
        botonTexto: 'Cambiar', datos: { id: mesa.idMesa, estado: nuevo },
        accion: (d) => this.cambiarEstado(d.id, d.estado),
      });
    },
    async cambiarEstado(id, estado) {
      const r = await cambiarEstadoMesa(id, estado);
      if (r.success) {
        await this.cargarMesas();
        this.mostrarToast('success', 'Actualizada', 'Estado cambiado correctamente.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'No se pudo cambiar el estado.');
      }
    },

    // ========== MODAL CONFIRMACIÓN ==========
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-blue-600 to-indigo-600';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    convertirEstadoATexto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }

@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }

@media (max-width: 768px) {
  button { min-height: 44px; }
  input, select, textarea { font-size: 16px !important; }
}
</style>